import styled, { keyframes } from 'styled-components'
import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import colors from '../utils/style/colors'
import { stockMeta } from '../utils/stockStatus'
import { supabase } from '../supabase'

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`
const bgShift = keyframes`
  from { filter: brightness(1); }
  to   { filter: brightness(1.1) saturate(1.1); }
`

// ─── MINI HERO ───
const MiniHero = styled.section`
  position: relative;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: ${colors.heroBg};
  @media (max-width: 768px) { height: 160px; }
`
const HeroBg = styled.div`
  position: absolute; inset: 0;
  background:
    radial-gradient(ellipse 80% 60% at 20% 60%, rgba(58,90,46,0.5) 0%, transparent 65%),
    radial-gradient(ellipse 70% 50% at 80% 40%, rgba(107,76,42,0.35) 0%, transparent 60%),
    linear-gradient(160deg, #0e1a0a 0%, #1e2e14 40%, #2a1a0a 100%);
  animation: ${bgShift} 10s ease-in-out infinite alternate;
`
const Grain = styled.div`
  position: absolute; inset: 0;
  pointer-events: none;
  opacity: 0.05;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
  background-size: 200px 200px;
`
const HeroContent = styled.div`
  position: relative; z-index: 2;
  text-align: center;
  animation: ${fadeUp} 0.8s both;
`
// const HeroBadge = styled.span`
//   display: inline-block;
//   background: rgba(200,168,75,0.15);
//   border: 1px solid rgba(200,168,75,0.4);
//   color: ${colors.orClair};
//   font-size: 0.7rem; font-weight: 700;
//   letter-spacing: 0.18em; text-transform: uppercase;
//   padding: 6px 16px; border-radius: 2px;
//   margin-bottom: 18px;
//   font-family: ${colors.font};
// `
const HeroTitle = styled.h1`
  font-family: 'Playfair Display', Georgia, serif;
  font-size: clamp(2rem, 5vw, 3.4rem);
  font-weight: 900; color: #fff;
  line-height: 1.1; margin: 0 0 14px;
  text-shadow: 0 4px 24px rgba(0,0,0,0.5);
  em { font-style: normal; color: ${colors.orClair}; }
`
const HeroSub = styled.p`
  font-family: ${colors.font};
  font-size: 1rem; color: rgba(255,255,255,0.65);
  font-weight: 300; margin: 0;
`
const HeroBar = styled.div`
  position: absolute; bottom: 0; left: 0; right: 0;
  height: 4px;
  background: linear-gradient(90deg, transparent, ${colors.or}, transparent);
`

// ─── BODY ───
const PageWrap = styled.div`
  background: ${colors.blanc};
  padding: 60px 48px 100px;
  @media (max-width: 768px) { padding: 40px 20px 80px; }
`
const FilterBar = styled.div`
  display: flex; gap: 10px; flex-wrap: wrap;
  margin-bottom: 48px; align-items: center;
`
const FilterLabel = styled.span`
  font-family: ${colors.font}; font-size: 0.75rem; font-weight: 600;
  letter-spacing: 0.12em; text-transform: uppercase;
  color: ${colors.texteLeger}; margin-right: 6px;
`
const FilterBtn = styled.button`
  font-family: ${colors.font}; font-size: 0.8rem; font-weight: 600;
  letter-spacing: 0.06em; text-transform: uppercase;
  padding: 8px 18px; border-radius: 3px; cursor: pointer;
  transition: all 0.2s;
  border: 1.5px solid ${(p) => p.$active ? colors.or : colors.beigeFonce};
  background: ${(p) => p.$active ? colors.or : 'transparent'};
  color: ${(p) => p.$active ? colors.texte : colors.texteLeger};
  &:hover { border-color: ${colors.or}; background: rgba(200,168,75,0.1); color: ${colors.texte}; }
`
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  @media (max-width: 1200px) { grid-template-columns: repeat(3, 1fr); }
  @media (max-width: 900px)  { grid-template-columns: repeat(2, 1fr); gap: 16px; }
  @media (max-width: 480px)  { grid-template-columns: 1fr; }
`
const Card = styled(Link)`
  text-decoration: none;
  background: #fff;
  border: 1px solid ${colors.beigeFonce};
  border-radius: 8px;
  overflow: hidden;
  display: flex; flex-direction: column;
  transition: box-shadow 0.3s, border-color 0.3s, transform 0.25s;
  animation: ${fadeUp} 0.5s ${(p) => p.$delay || '0s'} both;
  &:hover {
    box-shadow: 0 16px 48px rgba(58,90,46,0.14);
    border-color: ${colors.or};
    transform: translateY(-3px);
  }
`
const CardImg = styled.div`
  height: 200px;
  position: relative; overflow: hidden;
  background: ${(p) =>
    p.$cat === 'Paille' ? 'linear-gradient(135deg, rgba(200,168,75,0.13), #e8d5b8)' :
    p.$cat === 'Foin'   ? 'linear-gradient(135deg, rgba(90,138,69,0.13), #e8f0e4)' :
                          'linear-gradient(135deg, rgba(107,76,42,0.13), #f0e8dc)'};
`
const CardPhoto = styled.img`
  width: 100%; height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;
  ${Card}:hover & { transform: scale(1.05); }
`
const CardIconFallback = styled.div`
  width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center;
  font-size: 3.5rem;
`
const CardBadge = styled.span`
  position: absolute; top: 12px; left: 12px;
  background: ${colors.vertOlive}; color: #fff;
  font-family: ${colors.font}; font-size: 0.65rem; font-weight: 700;
  letter-spacing: 0.1em; text-transform: uppercase;
  padding: 4px 10px; border-radius: 2px;
`
const CardCatTag = styled.span`
  position: absolute; top: 12px; right: 12px;
  background: rgba(200,168,75,0.15);
  border: 1px solid rgba(200,168,75,0.35);
  color: ${colors.orClair};
  font-family: ${colors.font}; font-size: 0.62rem; font-weight: 700;
  letter-spacing: 0.1em; text-transform: uppercase;
  padding: 4px 10px; border-radius: 2px;
`
const CardBody = styled.div`
  padding: 22px 24px 24px;
  display: flex; flex-direction: column; flex: 1;
`
const CardTitle = styled.h3`
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 1.15rem; font-weight: 700;
  color: ${colors.texte}; margin: 0 0 16px;
`
const Specs = styled.div`
  display: flex; flex-direction: column;
  gap: 9px; margin-bottom: 20px; flex: 1;
`
const SpecRow = styled.div`
  display: flex; align-items: flex-start; gap: 10px;
`
const SpecIcon = styled.span`
  font-size: 0.85rem; width: 18px; flex-shrink: 0; margin-top: 1px;
`
const SpecLabel = styled.span`
  font-family: ${colors.font}; font-size: 0.72rem; font-weight: 700;
  color: ${colors.texteLeger}; letter-spacing: 0.06em;
  text-transform: uppercase; min-width: 88px; flex-shrink: 0;
`
const SpecVal = styled.span`
  font-family: ${colors.font}; font-size: 0.82rem;
  color: ${colors.texte}; font-weight: 500;
`
const UsageTags = styled.div`
  display: flex; flex-wrap: wrap; gap: 5px;
`
const UsageTag = styled.span`
  background: ${colors.beige}; color: ${colors.texteLeger};
  font-family: ${colors.font}; font-size: 0.7rem; font-weight: 600;
  padding: 3px 9px; border-radius: 12px;
`
const CardCTA = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding-top: 16px;
  border-top: 1px solid ${colors.beigeFonce};
  margin-top: auto;
`
const CardLinkText = styled.span`
  font-family: ${colors.font}; font-size: 0.78rem; font-weight: 600;
  color: ${colors.vertClair}; letter-spacing: 0.06em; text-transform: uppercase;
  display: flex; align-items: center; gap: 6px;
  transition: gap 0.2s, color 0.2s;
  ${Card}:hover & { gap: 10px; color: ${colors.or}; }
`
const DevisBtn = styled(Link)`
  font-family: ${colors.font}; font-size: 0.75rem; font-weight: 700;
  letter-spacing: 0.06em; text-transform: uppercase;
  text-decoration: none;
  background: ${colors.or}; color: ${colors.texte};
  padding: 8px 14px; border-radius: 3px;
  transition: all 0.2s;
  &:hover { background: ${colors.orClair}; transform: translateY(-1px); }
`

// ─── DONNÉES SUPABASE ───
const CATS = ['Tous', 'Paille', 'Foin']

function getCat(name) {
  if (name.toLowerCase().includes('paille')) return 'Paille'
  if (name.toLowerCase().includes('foin') || name.toLowerCase().includes('luzerne') || name.toLowerCase().includes('prairie')) return 'Foin'
  return 'Autre'
}

function getIcon(name) {
  if (name.toLowerCase().includes('luzerne')) return '🌱'
  if (name.toLowerCase().includes('foin') || name.toLowerCase().includes('prairie')) return '🌿'
  return '🌾'
}

function Produits() {
  const [filtre, setFiltre] = useState('Tous')
  const [produits, setProduits] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase
      .from('products')
      .select('*, product_images(*)')
      .order('created_at')
      .then(({ data }) => {
        setProduits(data || [])
        setLoading(false)
      })
  }, [])

  const liste = produits
    .filter(p => filtre === 'Tous' || getCat(p.name) === filtre)
    .map(p => ({
      id: p.id,
      cat: getCat(p.name),
      nom: p.name,
      icon: getIcon(p.name),
      description: p.description || '',
      image: p.product_images?.sort((a,b) => a.order_index - b.order_index)[0]?.url || null,
      format: p.format || '',
      dimensions: p.dimensions || '',
      poids: p.poids || '',
      coupe: p.coupes || [],
      usages: p.usages || [],
      stock: stockMeta(p),
    }))

  return (
    <>
      <MiniHero>
        <HeroBg />
        <Grain />
        <HeroContent>
          {/* <HeroBadge>🇫🇷 Origine France garantie</HeroBadge> */}
          <HeroTitle>Nos <em>produits</em></HeroTitle>
          <HeroSub>Paille, foin et luzerne — sélectionnés et livrés en Suisse</HeroSub>
        </HeroContent>
        <HeroBar />
      </MiniHero>

      <PageWrap>
        <FilterBar>
          <FilterLabel>Filtrer :</FilterLabel>
          {CATS.map((c) => (
            <FilterBtn key={c} $active={filtre === c} onClick={() => setFiltre(c)}>
              {c === 'Tous' ? 'Tous les produits' : c}
            </FilterBtn>
          ))}
        </FilterBar>

        {loading ? (
          <p style={{ fontFamily: colors.font, color: colors.texteLeger, textAlign: 'center', padding: '60px 0' }}>Chargement…</p>
        ) : (
        <Grid>
          {liste.map((p, i) => (
            <Card key={p.id} to={`/produits/${p.id}`} $delay={`${i * 0.08}s`}>
              <CardImg $cat={p.cat}>
                {p.image
                  ? <CardPhoto src={p.image} alt={p.nom} onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
                  : null}
                <CardIconFallback style={{ display: p.image ? 'none' : 'flex' }}>
                  {p.icon}
                </CardIconFallback>
                <CardBadge style={{ background: p.stock.badge }}>
                  {p.stock.label}
                </CardBadge>
                <CardCatTag>{p.cat}</CardCatTag>
              </CardImg>
              <CardBody>
                <CardTitle>{p.nom}</CardTitle>
                <Specs>
                  {p.format && (
                    <SpecRow>
                      <SpecIcon>📦</SpecIcon>
                      <SpecLabel>Format</SpecLabel>
                      <SpecVal>{p.format}</SpecVal>
                    </SpecRow>
                  )}
                  {p.dimensions && (
                    <SpecRow>
                      <SpecIcon>📐</SpecIcon>
                      <SpecLabel>Dimensions</SpecLabel>
                      <SpecVal>{p.dimensions} cm</SpecVal>
                    </SpecRow>
                  )}
                  {p.poids && (
                    <SpecRow>
                      <SpecIcon>⚖️</SpecIcon>
                      <SpecLabel>Poids moyen</SpecLabel>
                      <SpecVal>{p.poids}</SpecVal>
                    </SpecRow>
                  )}
                  {p.coupe.length > 0 && (
                    <SpecRow>
                      <SpecIcon>✂️</SpecIcon>
                      <SpecLabel>Coupe</SpecLabel>
                      <SpecVal>{p.coupe.join(', ')}</SpecVal>
                    </SpecRow>
                  )}
                  {p.usages.length > 0 && (
                    <SpecRow>
                      <SpecIcon>🐄</SpecIcon>
                      <SpecLabel>Usage</SpecLabel>
                      <UsageTags>
                        {p.usages.map((u) => <UsageTag key={u}>{u}</UsageTag>)}
                      </UsageTags>
                    </SpecRow>
                  )}
                </Specs>
                <CardCTA>
                  <CardLinkText>Voir le détail <span>→</span></CardLinkText>
                  <DevisBtn to="/devis" onClick={(e) => e.stopPropagation()}>
                    Devis →
                  </DevisBtn>
                </CardCTA>
              </CardBody>
            </Card>
          ))}
        </Grid>
        )}
      </PageWrap>
    </>
  )
}

export default Produits
